import React, { useEffect, useMemo, useState } from 'react';
import { fontList } from '@mnz/builder';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { useHistory, useLocation, useParams } from 'react-router';

import Header from '../../components/layout/Header';
import NavBar from './Navbar';

import ChooseFontModal from './modals/design/ChooseFontModal';
import DesignSettingModal from './modals/design/DesignSettingModal';
import BrowseThemeModal from './modals/design/BrowseThemeModal';
import ColorsModal from './modals/design/ColorsModal';
import StyleModal from './modals/design/StyleModal';
import { MODAL } from './modals/config';
import { useQuery } from '../../hooks/use-query';
import ChooseColorModal from './modals/design/ChooseColorModal';
import FontModal from './modals/design/FontModal';
import ZenLoading from '../../components/loading/Loading';
import DesignPreview from './DesignPreview';
import PublishModal from '../site-settings/modal/PublishModal';
import LoginDialog from '../site-settings/modal/LoginDialog';
import RenderNode from './setting/RenderNode';
import DesignView from './DesignView';
import ElementSettingDialog from './setting/ElementSettingDialog';
import { useGetSiteDesignQuery, useGetAccountQuery, useGetThemesQuery } from '../../generated/types';

import { DesignMode, Editor, fontFaceCSS } from '@mnz/builder';
import { Document, Container, Basic } from '@mnz/element';
import BlockModal from './modals/block/BlockModal';
import AddBlockModal from './modals/block/AddBlockModal';
import ComingSoonModal from './modals/block/ComingSoonModal';
import { isLoggedIn } from '../../shared/libs/auth/auth';
import ProfileModal from './modals/profile/ProfileModal';
import { AccountUpgradeModal } from '../../components/grow-plan/AccountUpgradeModal';

export const GlobalStyle = createGlobalStyle`
    ${fontFaceCSS}
`;

const SiteDesignPage: React.FC = () => {
    const history = useHistory();

    const query = useQuery();
    const location = useLocation();
    const { accountId } = useParams<{ accountId: string }>();
    const [modal, setModalValue] = useState<string | null>(query.get('modal'));
    const [mode, setModeValue] = useState<string | null>(query.get('mode'));
    const [openModalCommingSoon, setOpenModalComingSoon] = useState(false);

    const { data: accountData, loading: isLoadingAccount } = useGetAccountQuery({
        variables: {
            id: parseInt(accountId + '', 10),
        },
    });
    const { loading: isLoadingDesign, data: design } = useGetSiteDesignQuery();

    const { data: themeData } = useGetThemesQuery();

    useEffect(() => {
        setModalValue(query.get('modal'));
        setModeValue(query.get('mode'));
    }, [query]);

    const setMode = (newMode: string | null) => {
        if (newMode) {
            query.set('mode', newMode);
            if (!mode) {
                return history.push({
                    pathname: location.pathname,
                    search: `?${query.toString()}`,
                });
            }
        } else {
            query.delete('mode');
        }
        history.replace({
            pathname: location.pathname,
            search: `?${query.toString()}`,
        });
    };

    const setModal = (newModal: string | null) => {
        if (newModal) {
            query.set('modal', newModal);
            if (!modal) {
                return history.push({
                    pathname: location.pathname,
                    search: `?${query.toString()}`,
                });
            }
        } else {
            query.delete('modal');
        }
        history.replace({
            pathname: location.pathname,
            search: `?${query.toString()}`,
        });
    };
    const handleStartEditColor = (type: string) => {
        query.set('type', type);
        history.replace({
            pathname: location.pathname,
            search: `?${query.toString()}`,
        });
    };
    const handleEndEditColor = () => {
        query.delete('type');
        history.replace({
            pathname: location.pathname,
            search: `?${query.toString()}`,
        });
    };
    const handleStartChooseFont = (type: string) => {
        query.set('type', type);
        history.replace({
            pathname: location.pathname,
            search: `?${query.toString()}`,
        });
    };
    const handleEndChooseFont = () => {
        query.delete('type');
        history.replace({
            pathname: location.pathname,
            search: `?${query.toString()}`,
        });
    };

    const colorType = useMemo(() => {
        return ['background', 'text', 'button', 'buttonText', 'socialButton'].includes(
            query.get('type') || 'background',
        )
            ? query.get('type')
            : 'background';
    }, [query]) as string;

    const fontType = useMemo(() => {
        return query.get('type') ?? 'primary';
    }, [query]) as 'primary' | 'secondary';

    const onClickPreview = () => {
        setMode('PREVIEW');
    };

    const onClickPublish = async () => {
        if (!isLoggedIn()) {
            setModal(MODAL.LOGIN);
            return;
        }
        setModal(MODAL.PUBLISH);
    };

    const themeId = useMemo(() => (query.get('theme') ? parseInt(query.get('theme') as string, 10) : null), [query]);

    const handleClickPreview = () => {
        if (!accountId) {
            history.push(`/site-design?mode=PREVIEW`);
        } else {
            history.push(`/account/${accountId}/site-design?mode=PREVIEW`);
        }
    };

    const successUrl = useMemo(() => {
        const theUrl = new URL(window.location.href);
        theUrl.searchParams.delete('modal');
        return theUrl.href;
    }, [modal]);

    if (isLoadingDesign || isLoadingAccount) {
        return (
            <div className="flex items-center justify-center flex-auto">
                <ZenLoading />
            </div>
        );
    }

    if (!design?.site || !accountData?.account) {
        history.push(`/account/${accountId}/home`);
        return <div></div>;
    }

    return (
        <>
            <ThemeProvider theme={{ fontEndpoint: process.env.REACT_APP_FONT_PATH }}>
                <GlobalStyle />
            </ThemeProvider>

            <Editor
                resolver={{ Document, Container, ...Basic }}
                onRender={RenderNode}
                enabled={mode !== 'PREVIEW'}
                mode={DesignMode.EDIT}
            >
                {mode === 'PREVIEW' ? (
                    <DesignPreview
                        data={design.site.elements}
                        designSettings={design.site.designSettings}
                        account={accountData.account}
                        onBack={() => setMode(null)}
                        onClickPublish={onClickPublish}
                        isPublishing={false}
                    />
                ) : (
                    <div className="flex flex-col flex-auto px-30 bg-grey-light-extra overflow-y-auto pt-28">
                        <Header
                            className={`justify-between`}
                            showAction
                            onBack={
                                isLoggedIn()
                                    ? () => {
                                          history.push(`/account/${accountId}/home`);
                                      }
                                    : undefined
                            }
                            onClickSecondary={onClickPreview}
                            onClickPrimary={onClickPublish}
                            isPrimaryLoading={false}
                        />
                        <div className="flex-auto shadow-designCard rounded-10 overflow-hidden overflow-y-auto mt-30">
                            <DesignView
                                data={design.site.elements}
                                designSettings={design.site.designSettings}
                                account={accountData.account}
                                openBlockModal={() => setModal(MODAL.BLOCK_SETTING)}
                            />
                            <ElementSettingDialog setModal={setModal} />
                        </div>
                        <NavBar onClickItem={setModal} />
                        <DesignSettingModal
                            title={'Design'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.DESIGN_SETTING}
                            onClickItem={setModal}
                            showClose={true}
                            onClose={() => setModal(null)}
                        />
                        <BrowseThemeModal
                            title={'Choose Theme'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.CHOOSE_THEME}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => setModal(MODAL.DESIGN_SETTING)}
                            themes={themeData?.siteThemes || []}
                            currentThemeId={themeId}
                        />
                        <ColorsModal
                            title={'Colors'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.COLOR_SETTING}
                            onSelectItem={(type) => {
                                handleStartEditColor(type);
                                setModal(MODAL.CHOOSE_COLOR);
                            }}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => {
                                setModal(MODAL.DESIGN_SETTING);
                            }}
                        />
                        <ChooseColorModal
                            title="Select Color"
                            className={`hide-backdrop`}
                            open={modal === MODAL.CHOOSE_COLOR}
                            type={colorType}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => {
                                handleEndEditColor();
                                setModal(MODAL.COLOR_SETTING);
                            }}
                        />
                        <ChooseColorModal
                            title="Background"
                            className={`hide-backdrop`}
                            open={modal === MODAL.BACKGROUND_SETTING}
                            type="background"
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => {
                                handleEndEditColor();
                                setModal(MODAL.DESIGN_SETTING);
                            }}
                        />
                        <ChooseFontModal
                            title={'Select Font'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.CHOOSE_FONT}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => {
                                handleEndChooseFont();
                                setModal(MODAL.FONT);
                            }}
                            type={fontType}
                        />
                        <FontModal
                            title={'Fonts'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.FONT}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => setModal(MODAL.DESIGN_SETTING)}
                            onSelectFontType={(type) => {
                                handleStartChooseFont(type);
                                setModal(MODAL.CHOOSE_FONT);
                            }}
                        />
                        <StyleModal
                            title={'Style'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.STYLE_SETTING}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => setModal(MODAL.DESIGN_SETTING)}
                        />
                        <BlockModal
                            className={`hide-backdrop`}
                            open={modal === MODAL.BLOCK_SETTING}
                            showClose={false}
                            onClose={() => setModal(null)}
                            setModal={setModal}
                        />
                        <AddBlockModal
                            title={'Add New Block'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.ADD_BLOCK}
                            showClose={true}
                            onClose={() => setModal(null)}
                            onBack={() => setModal(MODAL.BLOCK_SETTING)}
                            setOpenModalComingSoon={() => setOpenModalComingSoon(true)}
                            setModal={setModal}
                        />
                        <ComingSoonModal open={openModalCommingSoon} onClose={() => setOpenModalComingSoon(false)} />
                        <ProfileModal
                            title={'Business Profile'}
                            className={`hide-backdrop`}
                            open={modal === MODAL.PROFILE_SETTING}
                            showClose={true}
                            onClose={() => setModal(null)}
                        />
                        <AccountUpgradeModal
                            open={modal === MODAL.UPGRADE_ACCOUNT}
                            anchor={`bottom`}
                            onClose={() => setModal(null)}
                            showClose={true}
                            showBarIcon={false}
                            cancelUrl={window.location.href}
                            successUrl={successUrl}
                        />
                        {Object.keys(fontList).map((font, index) => {
                            return (
                                <div key={index} className="invisible fixed left-full" style={{ fontFamily: font }}>
                                    {font}
                                </div>
                            );
                        })}
                    </div>
                )}
                {design && (
                    <PublishModal
                        open={modal === MODAL.PUBLISH}
                        onClose={() => {
                            setModal('');
                        }}
                        username={accountData.account.username}
                    />
                )}
                {design && (
                    <LoginDialog
                        open={modal === MODAL.LOGIN}
                        onClose={() => {
                            setModal('');
                        }}
                        onClickPreview={handleClickPreview}
                        claimToken={accountData.account.claimToken || ''}
                    />
                )}
            </Editor>
        </>
    );
};

export default SiteDesignPage;
